import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import { US_PHONE_MASK } from '@nesso/ui/primitives/masked-input-field';

import {
    businessEmailSchema,
    businessPhoneSchema,
    MaskedTaxId,
    practiceDetailsOptions,
    practiceNameSchema,
    taxIdSchema,
    useUpdatePracticeOptions,
} from '@/entities/practice';
import { formatPhone } from '@/shared/lib/format-utils';
import { SettingsEditableRow } from '@/widgets/settings/settings-editable-row';
import { SettingsSection } from '@/widgets/settings/settings-section';

import type { PracticeDetailsEditing } from '../../model/use-practice-details-editing';

type InfoField = 'name' | 'businessEmail' | 'businessPhone' | 'taxId';
const infoFieldKey = (field: InfoField) => `info:${field}`;

type Props = {
    editing: PracticeDetailsEditing;
};

export function PracticeInformationSection({ editing }: Props) {
    const { data: practice } = useSuspenseQuery(practiceDetailsOptions());
    const updateMutation = useMutation(useUpdatePracticeOptions());

    const isDisabled = (field: InfoField) => editing.isDisabled(infoFieldKey(field));
    const onEditingChange = (field: InfoField) => editing.onEditingChange(infoFieldKey(field));

    // Named handlers, not inline arrows: `onSave` sits inside a discriminated union keyed on
    // `schema`, and TypeScript can't narrow that JSX prop union before typing an inline callback.
    const handleSaveName = (name: string) => updateMutation.mutateAsync({ name });
    const handleSaveBusinessEmail = (businessEmail: string) =>
        updateMutation.mutateAsync({ businessEmail });
    const handleSaveBusinessPhone = (businessPhone: string | null) =>
        updateMutation.mutateAsync({ businessPhone });
    const handleSaveTaxId = (taxId: string) => updateMutation.mutateAsync({ taxId });
    const renderMaskedTaxId = (taxId: string) => <MaskedTaxId taxId={taxId} />;

    return (
        <SettingsSection title="Practice information" className="mb-12">
            <SettingsEditableRow
                label="Practice name"
                value={practice.name}
                schema={practiceNameSchema}
                onSave={handleSaveName}
                isDisabled={isDisabled('name')}
                onEditingChange={onEditingChange('name')}
            />
            <SettingsEditableRow
                label="Business email"
                value={practice.businessEmail}
                schema={businessEmailSchema}
                onSave={handleSaveBusinessEmail}
                isDisabled={isDisabled('businessEmail')}
                onEditingChange={onEditingChange('businessEmail')}
            />
            <SettingsEditableRow
                label="Business phone"
                value={formatPhone(practice.businessPhone)}
                description="Optional"
                schema={businessPhoneSchema}
                mask={US_PHONE_MASK}
                placeholder="(   )    -    "
                inputMode="tel"
                inputClassName="max-w-43"
                onSave={handleSaveBusinessPhone}
                isDisabled={isDisabled('businessPhone')}
                onEditingChange={onEditingChange('businessPhone')}
                isOptional
            />
            <SettingsEditableRow
                label="Tax ID"
                value={practice.taxId}
                description="9-digit ID number included on all superbills. To protect your identity, we recommend using your EIN, not your SSN."
                schema={taxIdSchema}
                inputFilter={(value) => value.replace(/[^0-9-]/g, '')}
                maxLength={11}
                inputMode="numeric"
                renderValue={renderMaskedTaxId}
                onSave={handleSaveTaxId}
                isDisabled={isDisabled('taxId')}
                onEditingChange={onEditingChange('taxId')}
            />
        </SettingsSection>
    );
}
